// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import { bool, func, number, oneOfType, string, object } from 'prop-types';
import { Button, Loading } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { ShoppingCart } from 'lucide-react';

import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import useAnalytics from 'Hooks/useAnalytics';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSION } from 'Constants';

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .quantitySelector {
    margin-right: 10px;

    input {
      height: 100%;
    }
  }

  .addToCart {
    svg {
      margin-right: 12px;
      height: 16px;
    }

    .cds--loading {
      position: absolute;
      left: 10px;
      top: calc(50% - 0.5rem);
      margin: 0 0 0 5px;
      width: 1rem;
      height: 1rem;
    }

    min-height: 0;
    min-width: 165px;
    max-width: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${ props => props.theme.colors.white };
    padding: 0;

    ${ props => {
      if (props.size === 'xl') {
        return `
          height: 50px;
          width: 280px;
          font-size: 1.125rem;
        `;
      }

      if (props.size === 'md') {
        return `
          height: 40px;
          width: 200px;
          font-size: 1rem;
        `;
      }

      if (props.size === 'sm') {
        return `
          height: 30px;
          width: 165px;
          font-size: 1rem;
        `;
      }

      return `
        height: 40px;
        width: 200px;
        font-size: 1.125rem;
      `;
    } }
  }
`;

const AddToCart = forwardRef((props, ref) => {
  const {
    onChange,
    onClick,
    value = 1,
    min = 1,
    max = 99,
    step = 1,
    size = 'lg',
    showSelector = true,
    label = '',
    disabled = false,
    loading = false,
    id,
    trackingContext = {},
    analyticsEvent = 'add_to_cart',
    requiresAddToCartPermission = true
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const analytics = useAnalytics();
  const { can } = usePermissions();
  const canAddToCart = can(PERMISSION.ADD_TO_CART);

  const handleOnClick = event => {
    analytics.track(analyticsEvent, {
      product_id: id,
      quantity: value,
      ...(trackingContext.location === 'product_table_view' ? {
        quantity: '',
        location: trackingContext.location,
        ...trackingContext.products
      } : { ...trackingContext })
    });
    onClick(event);
  };

  return (
    <StyledContainer
      className="addToCartContainer"
      ref={ ref }
      theme={ theme }
      size={ size }
    >
      { showSelector && (
        <QuantitySelect
          onChange={ onChange }
          value={ value }
          min={ min }
          step={ step }
          max={ max }
          size="lg"
          data-test="onChangeQuantity"
          data-testid={ id }
        />
      ) }
      <Button
        className="addToCart"
        kind="danger"
        onClick={ handleOnClick }
        data-test="onAddToCart"
        data-testid={ id }
        disabled={ disabled || (requiresAddToCartPermission && !canAddToCart) }
      >
        { loading && <Loading active small withOverlay={ false } /> }
        <ShoppingCart />
        { label || t('products.addToCart') }
      </Button>
    </StyledContainer>
  );
});

AddToCart.propTypes = {
  id: oneOfType([string, number]),
  showSelector: bool,
  onClick: func,
  onChange: func,
  disabled: bool,
  loading: bool,
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  size: string,
  trackingContext: object,
  analyticsEvent: string,
  requiresAddToCartPermission: bool,
};
AddToCart.displayName = 'AddToCart';

export default AddToCart;
